import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Add } from '@mui/icons-material';
import { FormState } from 'final-form';
import { omit } from 'lodash-es';

import Loadable from '@components/Loadable/Loadable';
import Modal from '@components/Modal/Modal';
import { TInquiryStatus } from '@interfaces/inquiries';
import { IUserEnteredMedicationData } from '@interfaces/medications';
import { fileUploadProgressSelector } from '@store/features/fileUploadState/selectors';
import { inquiryDetailsSelector } from '@store/features/inquiryDraft/inquiryDetails/selectors';
import {
  handleCreateNewUserEnteredMedication,
  handleEditUserEnteredMedication,
  handleGetUserEnteredMedicationDetails,
} from '@store/features/medications/userEnteredMedication/actions';
import {
  createdUserEnteredMedicationSelector,
  userEnteredMedicationErrorSelector,
  userEnteredMedicationIsLoadingSelector,
  userEnteredMedicationSelector,
} from '@store/features/medications/userEnteredMedication/selectors';
import { removeNewUserEnteredMedication } from '@store/features/medications/userEnteredMedication/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import UserEnteredMedicationForm from './UserEnteredMedicationForm/UserEnteredMedicationForm';
import { IUserEnteredMedicationModalProps } from './UserEnteredMedicationModal.interface';

import './style.scss';

const UserEnteredMedicationModal: FC<IUserEnteredMedicationModalProps> = (
  props: IUserEnteredMedicationModalProps,
) => {
  const { medicationId, open, onClose, onExited, onAddToInquiry } = props;
  const { t } = useTranslation();
  const eventBus = useEventBus('userEnteredMedicationModal');

  const dispatch = useDispatch();
  const [formState, setFormState] = useState<FormState<IUserEnteredMedicationData>>();
  const userEnteredMedicationResult = useSelector(createdUserEnteredMedicationSelector);
  const userEnteredMedication = useSelector(userEnteredMedicationSelector);
  const userEnteredMedicationIsLoading = useSelector(userEnteredMedicationIsLoadingSelector);
  const userEnteredMedicationError = useSelector(userEnteredMedicationErrorSelector);
  const inquiryDetails = useSelector(inquiryDetailsSelector);
  const uploadingFilesInProgress = useSelector(fileUploadProgressSelector);

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const disabled = !!inquiryDetails && inquiryDetails?.inquiryStatusId !== TInquiryStatus.Draft;

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const isRightActionDisabled =
    formState?.hasValidationErrors ||
    userEnteredMedicationIsLoading ||
    disabled ||
    uploadingFilesInProgress;

  const initialValues = useMemo(
    () => (userEnteredMedication ? omit(userEnteredMedication, ['id']) : {}),
    [userEnteredMedication],
  );

  const handleFetchUserEnteredMedication = useCallback(() => {
    if (medicationId) {
      dispatch(handleGetUserEnteredMedicationDetails({ id: medicationId }));
    }
  }, [dispatch, medicationId]);

  const handleClose = useCallback((): void => {
    dispatch(removeNewUserEnteredMedication());
    onClose();
  }, [dispatch, onClose]);

  const handleValidate = useCallback((values: IUserEnteredMedicationData) => {
    const errors: Partial<Record<keyof IUserEnteredMedicationData, string | boolean>> = {};
    if (!values.name) {
      errors.name = true;
    }
    return errors;
  }, []);

  const handleSubmit = useCallback(
    (values: IUserEnteredMedicationData) => {
      if (isRightActionDisabled) {
        return;
      }
      if (medicationId) {
        return dispatch(
          handleEditUserEnteredMedication({
            id: medicationId,
            ...values,
          }),
        );
      }
      return dispatch(handleCreateNewUserEnteredMedication(values));
    },
    [dispatch, medicationId, isRightActionDisabled],
  );

  useEffect(() => {
    handleFetchUserEnteredMedication();
  }, [handleFetchUserEnteredMedication]);

  useEffect(() => {
    if (open && userEnteredMedicationResult) {
      onAddToInquiry(userEnteredMedicationResult.id);
    }
    dispatch(removeNewUserEnteredMedication());
  }, [userEnteredMedicationResult]);

  useEffect(() => {
    eventBus.on(EventType.CREATE_NEW_MEDICATION_SUCCESS, () =>
      handleToast('success', t('addMedicationModal.medicationCreatedSuccessfully')),
    );

    eventBus.on(EventType.EDIT_NEW_MEDICATION_SUCCESS, () =>
      handleToast('success', t('addMedicationModal.medicationEditedSuccessfully')),
    );

    eventBus.on(
      [
        EventType.CREATE_NEW_INQUIRY_SUCCESS,
        EventType.ADD_NEW_MEDICATION_TO_INQUIRY_SUCCESS,
        EventType.EDIT_NEW_MEDICATION_SUCCESS,
      ],
      handleClose,
    );

    return () => {
      eventBus.off([
        EventType.CREATE_NEW_INQUIRY_SUCCESS,
        EventType.CREATE_NEW_MEDICATION_SUCCESS,
        EventType.EDIT_NEW_MEDICATION_SUCCESS,
        EventType.ADD_NEW_MEDICATION_TO_INQUIRY_SUCCESS,
      ]);
    };
  }, []);

  return (
    <Modal
      onClose={handleClose}
      onExited={onExited}
      open={open}
      title={medicationId ? t('addMedicationModal.editTitle') : t('addMedicationModal.createTitle')}
      className="user-entered-medication-modal"
      leftActionTitle={t('common.cancel')}
      onLeftAction={handleClose}
      rightActionIcon={<Add />}
      rightActionTitle={
        medicationId
          ? t('addMedicationModal.saveMedication')
          : t('addMedicationModal.addMedication')
      }
      isRightActionLoading={formState?.submitting}
      isRightActionDisabled={isRightActionDisabled}
      submitFormId="user-entered-medication-form"
    >
      <Loadable
        isLoading={userEnteredMedicationIsLoading}
        error={userEnteredMedicationError && t('errors.userEnteredMedicationError')}
        onRetry={handleFetchUserEnteredMedication}
      >
        <Form initialValues={initialValues} validate={handleValidate} onSubmit={handleSubmit}>
          {props => (
            <>
              <UserEnteredMedicationForm {...props} disabled={disabled} />
              <FormSpy
                subscription={{ submitting: true, hasValidationErrors: true }}
                onChange={setFormState}
              />
            </>
          )}
        </Form>
      </Loadable>
    </Modal>
  );
};

export default UserEnteredMedicationModal;
